import { CSSProperties, FC, MouseEvent, ReactNode } from 'react';
import type { Editor } from '@tiptap/react';
import { Button, Divider, Flex } from 'antd';
import {
    TbAlignCenter,
    TbAlignJustified,
    TbAlignLeft,
    TbAlignRight,
    TbArrowBack,
    TbArrowForward,
    TbBold,
    TbClearFormatting,
    TbCode,
    TbHighlight,
    TbItalic,
    TbList,
    TbListCheck,
    TbListNumbers,
    TbPageBreak,
    TbSourceCode,
    TbStrikethrough,
    TbUnderline,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';

import RichTextFormatDropdown from '../RichTextFormatDropdown';

export interface RichTextToolbarAction {
    icon: ReactNode;
    isActive?: () => boolean;
    label: string;
    onClick: () => void;
}

export interface RichTextToolbarProps {
    additionalInlineActions?: RichTextToolbarAction[];
    afterFormat?: ReactNode;
    afterPrimaryActions?: ReactNode;
    disabled?: boolean;
    editor: Editor;
    padding?: CSSProperties['padding'];
    trailing?: ReactNode;
}

const RichTextToolbar: FC<RichTextToolbarProps> = ({
    additionalInlineActions = [],
    afterFormat,
    afterPrimaryActions,
    disabled,
    editor,
    padding = '0.25rem',
    trailing,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const iconStyle = { color: token.colorTextTertiary };
    const actionGroups: RichTextToolbarAction[][] = [
        [
            {
                icon: <TbBold style={iconStyle} />,
                isActive: () => editor.isActive('bold'),
                label: 'Bold',
                onClick: () => editor.chain().focus().toggleBold().run(),
            },
            {
                icon: <TbItalic style={iconStyle} />,
                isActive: () => editor.isActive('italic'),
                label: 'Italic',
                onClick: () => editor.chain().focus().toggleItalic().run(),
            },
            {
                icon: <TbUnderline style={iconStyle} />,
                isActive: () => editor.isActive('underline'),
                label: 'Underline',
                onClick: () => editor.chain().focus().toggleUnderline().run(),
            },
            {
                icon: <TbStrikethrough style={iconStyle} />,
                isActive: () => editor.isActive('strike'),
                label: 'Strikethrough',
                onClick: () => editor.chain().focus().toggleStrike().run(),
            },
            {
                icon: <TbCode style={iconStyle} />,
                isActive: () => editor.isActive('code'),
                label: 'Code',
                onClick: () => editor.chain().focus().toggleCode().run(),
            },
            {
                icon: <TbHighlight style={iconStyle} />,
                isActive: () => editor.isActive('highlight'),
                label: 'Highlight',
                onClick: () => editor.chain().focus().toggleHighlight().run(),
            },
            ...additionalInlineActions,
        ],
        [
            {
                icon: <TbList style={iconStyle} />,
                isActive: () => editor.isActive('bulletList'),
                label: 'Bullet List',
                onClick: () => editor.chain().focus().toggleBulletList().run(),
            },
            {
                icon: <TbListNumbers style={iconStyle} />,
                isActive: () => editor.isActive('orderedList'),
                label: 'Ordered List',
                onClick: () => editor.chain().focus().toggleOrderedList().run(),
            },
            {
                icon: <TbListCheck style={iconStyle} />,
                isActive: () => editor.isActive('taskList'),
                label: 'Task List',
                onClick: () => editor.chain().focus().toggleTaskList().run(),
            },
            {
                icon: <TbSourceCode style={iconStyle} />,
                isActive: () => editor.isActive('codeBlock'),
                label: 'Code Block',
                onClick: () => editor.chain().focus().toggleCodeBlock().run(),
            },
        ],
        [
            {
                icon: <TbPageBreak style={iconStyle} />,
                label: 'Horizontal Rule',
                onClick: () => editor.chain().focus().setHorizontalRule().run(),
            },
        ],
        [
            {
                icon: <TbAlignLeft style={iconStyle} />,
                isActive: () => editor.isActive({ textAlign: 'left' }),
                label: 'Left Align',
                onClick: () => editor.chain().focus().setTextAlign('left').run(),
            },
            {
                icon: <TbAlignCenter style={iconStyle} />,
                isActive: () => editor.isActive({ textAlign: 'center' }),
                label: 'Center Align',
                onClick: () => editor.chain().focus().setTextAlign('center').run(),
            },
            {
                icon: <TbAlignRight style={iconStyle} />,
                isActive: () => editor.isActive({ textAlign: 'right' }),
                label: 'Right Align',
                onClick: () => editor.chain().focus().setTextAlign('right').run(),
            },
            {
                icon: <TbAlignJustified style={iconStyle} />,
                isActive: () => editor.isActive({ textAlign: 'justify' }),
                label: 'Justify Align',
                onClick: () => editor.chain().focus().setTextAlign('justify').run(),
            },
        ],
    ];
    const trailingGroups: RichTextToolbarAction[][] = [
        [
            {
                icon: <TbClearFormatting style={iconStyle} />,
                label: 'Clear Formatting',
                onClick: () => editor.chain().focus().clearNodes().unsetAllMarks().run(),
            },
        ],
        [
            {
                icon: <TbArrowBack style={iconStyle} />,
                label: 'Undo',
                onClick: () => editor.chain().focus().undo().run(),
            },
            {
                icon: <TbArrowForward style={iconStyle} />,
                label: 'Redo',
                onClick: () => editor.chain().focus().redo().run(),
            },
        ],
    ];

    const preventFocusLoss = (event: MouseEvent<HTMLElement>) => event.preventDefault();
    const renderAction = (action: RichTextToolbarAction) => (
        <SmallTooltip key={action.label} title={action.label}>
            <Button
                aria-label={action.label}
                disabled={disabled}
                icon={action.icon}
                onClick={action.onClick}
                onMouseDown={preventFocusLoss}
                size='small'
                style={{ background: action.isActive?.() ? token.colorBorder : undefined }}
                type='text'
            />
        </SmallTooltip>
    );

    return (
        <Flex align='center' role='toolbar' style={{ minWidth: 0, overflowX: 'auto', padding }} wrap='nowrap'>
            <RichTextFormatDropdown disabled={disabled} editor={editor} />
            {afterFormat ? (
                <>
                    <Divider type='vertical' />
                    {afterFormat}
                </>
            ) : null}
            {actionGroups.map((group, index) => (
                <Flex align='center' key={`primary-${index}`}>
                    <Divider type='vertical' />
                    {group.map(renderAction)}
                </Flex>
            ))}
            {afterPrimaryActions ? (
                <Flex align='center'>
                    <Divider type='vertical' />
                    {afterPrimaryActions}
                </Flex>
            ) : null}
            <span aria-hidden style={{ flex: '1 1 auto', minWidth: '0.5rem' }} />
            {trailingGroups.map((group, index) => (
                <Flex align='center' key={`trailing-${index}`}>
                    <Divider type='vertical' />
                    {group.map(renderAction)}
                </Flex>
            ))}
            {trailing ? (
                <>
                    <Divider type='vertical' />
                    {trailing}
                </>
            ) : null}
        </Flex>
    );
};

export default RichTextToolbar;
